import { findRow, formatPrice } from "@/data/pricing";
import { pageFaqAdditions } from "@/data/pageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  absoluteUrl,
  breadcrumbSchema,
  canonicalLinks,
  faqPageSchema,
  jsonLd,
  ogImage,
  ogUrl,
  serviceSchema,
  webPageSchema,
} from "@/lib/seo";
import { LocationPage } from "@/components/site/LocationPage";

const answerPrice = (name: string) => formatPrice(findRow(name));

const PATH = "/fractional-coo-perth";
const SERVICE_ID = `${absoluteUrl(PATH)}#service`;
const TITLE = "Fractional COO Perth | Ajility";
const DESCRIPTION =
  "Fractional COO support for founder-led Perth businesses doing $3M to $30M. Mining services, engineering and contracting, on site in blocks.";

const faqs = [
  {
    q: "How does the time difference work with an east coast operator?",
    a: "It works in your favour more often than not. The two-hour or three-hour gap means the remote leadership session lands early in the Perth day, before site issues take over, and written work produced in the eastern afternoon is waiting when the Perth team starts. The cadence is set to Perth time, not Sydney time.",
  },
  {
    q: "How often are you physically in Perth?",
    a: "A Perth retainer runs a three-day on-site block roughly monthly, with weekly remote leadership sessions in between. Blocks are booked for the full engagement at kickoff. For a fixed-scope install the work is compressed into two longer blocks rather than spread across months.",
  },
  {
    q: "Do you understand mining services contracting?",
    a: "Yes. A significant part of Adam's operating career was spent in mining services, including a 90-day commercial reset that removed roughly $1M of annualised overhead and secured a multi-million-dollar contract variation. Rates and schedules, variation discipline, mobilisation cost and roster utilisation are familiar territory.",
  },
  {
    q: "Is a Perth engagement priced differently?",
    a: "No. The published catalogue applies nationally. The Operating Diagnostic is from $3,600 plus GST, the Full Diagnostic and 90-Day Roadmap is from $6,900 plus GST for one entity, and most composed retainers for businesses under $15M start from $3,900 per month plus GST. Travel time is absorbed in the price; for interstate work, flights and accommodation are arranged by the client or charged at cost.",
  },
  ...pageFaqAdditions["/fractional-coo-perth"],
];

export const Route = createFileRoute("/fractional-coo-perth")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Fractional COO and GM Perth | Ajility" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Fractional COO and GM Perth | Ajility" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Fractional COO in Perth for founder-led businesses" },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("fractional-coo-perth", "Fractional COO services in Perth from Ajility"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Fractional COO Perth", path: PATH }])),
      jsonLd(
        webPageSchema({ image: "fractional-coo-perth",
          path: PATH,
          name: TITLE,
          description: DESCRIPTION,
          mainEntityId: SERVICE_ID,
        }),
      ),
      jsonLd({
        ...serviceSchema({
          name: "Fractional COO Perth",
          description: DESCRIPTION,
          path: PATH,
          slug: "fractional-coo-perth",
          serviceType: "Fractional COO",
          offerId: ["operational-diagnostic", "full-diagnostic-roadmap", "fortnightly-rhythm-base"],
        }),
        areaServed: [
          { "@type": "City", name: "Perth" },
        ],
      }),
      jsonLd(faqPageSchema(PATH, faqs, SERVICE_ID)),
    ],
  }),
  component: PerthPage,
});

function PerthPage() {
  return (
    <LocationPage
      city="Perth"
      region="WA"
      heading="Fractional COO in Perth"
      lead={`Ajility works on site in Perth and remotely across Australia, mapping processes, removing bottlenecks and training teams on SOPs. The Systems & Operational Improvement install is ${answerPrice("Systems & Operational Improvement install")}. Operating leadership for Western Australian founders in mining services, engineering and contracting, where a contract term or a roster decision moves more margin than a sales campaign ever will. Most Australian businesses call this seat the general manager; a fractional GM and a fractional COO are the same role.`}
      intro={[
        "Perth is a contract economy. A large share of founder-led businesses between $3M and $30M here earn their revenue under someone else's terms: a schedule of rates, a services agreement with a tier one contractor, a panel arrangement with a resources company. The commercial skill that matters is not winning work. It is running work profitably inside terms that were written by the other side.",
        "That produces a specific pattern. Revenue is lumpy, mobilisation and demobilisation eat cash, variations are worth more than new sales and are claimed last, and the overhead built for the last boom is still sitting in the business two years after the work that justified it finished. Meanwhile the labour market punishes any business without a real roster plan, because the mine sites can always pay more.",
        "Ajility works with Perth founders on the operating disciplines that decide whether a contract book converts to profit: variation and claim discipline, roster and utilisation visibility, overhead that is deliberately sized, and a leadership rhythm that keeps all three in view weekly rather than at the end of a job.",
      ]}
      industries={[
        {
          name: "Mining and resources services",
          note: "Maintenance, shutdowns, labour hire, drilling support, camp services and equipment hire. Margin is decided by mobilisation cost, roster utilisation and how disciplined the business is about claiming variations while the evidence is still fresh. Most founder-led providers leave real money on the table in the last of those three.",
        },
        {
          name: "Engineering and fabrication",
          note: "Workshops through Kwinana, Malaga, Welshpool and Bibra Lake. Quote-to-actual comparison is the highest-value missing report. Where the workshop rate has not been rebuilt since input costs moved, every quoted hour is subsidising the customer.",
        },
        {
          name: "Civil and building contracting",
          note: "Residential, commercial and civil contractors managing subcontractor risk, retentions and progress claims. Working capital, not profit, is usually what limits how much work the business can safely carry at once.",
        },
        {
          name: "Transport, logistics and heavy haulage",
          note: "Long-haul and regional operators servicing the resources corridors. Cost per kilometre, backload utilisation, maintenance cost per unit and driver availability are the four numbers that matter, and they are rarely on one page.",
        },
        {
          name: "Trade and industrial services",
          note: "Electrical, mechanical, hydraulic and safety services businesses that scaled on the back of one or two large clients. Client concentration is the standing risk, and the operating answer is a delivery model that can absorb the loss of the largest contract without collapsing.",
        },
      ]}
      sections={[
        {
          heading: "How Perth engagements run",
          paragraphs: [
            "Perth runs on a block-plus-cadence model. A three-day on-site block, roughly monthly, covers the leadership session, the site or workshop walk, the commercial review and the one-to-one work with the leaders carrying quarterly priorities. Between blocks the weekly leadership session runs remotely at a fixed time set to Perth hours.",
            "The time difference is treated as a design feature rather than a problem. A leadership session at eight in the morning Perth time is a mid-morning session on the east coast, which means it happens before the day gets away from either party. Written work, models, packs and scorecard rebuilds are produced in the eastern afternoon and are waiting at the start of the next Perth day.",
            "Where the work is a fixed-scope install rather than an ongoing retainer, it is compressed into two longer blocks with a defined remote period between them. Sites outside the metropolitan area, including the Pilbara, the Goldfields and the South West, are visited where the operating truth is genuinely on site and cannot be read from Perth. Travel time is absorbed in the price; for interstate work, flights and accommodation are arranged by the client or charged at cost.",
          ],
        },
        {
          heading: "What tends to be wrong in a WA contracting business",
          paragraphs: [
            "The first thing is variation discipline. Scope creep on a mining services contract is constant, and the difference between a business that claims variations within the notice period and one that raises them at the end of the job is often the entire net margin. This is an administrative rhythm problem, not a legal one.",
            "The second is overhead that was sized for a previous cycle. Support roles, leases, equipment finance and standing costs accumulate during a strong period and are almost never deliberately resized when the work profile changes. A structured overhead review early in an engagement is regularly the fastest single improvement available.",
            "The third is the founder as the only commercial mind in the building. Site leads run their crews well and are given no visibility of the contract economics they are working inside. Once crew leaders can see the rates, the hours and the recovery on their own jobs, decisions that used to wait for the founder start being made correctly on site.",
            <>For the trades version of the operating seat, read <Link to="/insights/$slug" params={{ slug: "hire-a-general-manager-or-a-fractional-one" }} className="underline underline-offset-4">should I hire a general manager or a fractional one</Link>.</>,
          ],
        },
      ]}
      example={{
        label: "Anonymised Perth example",
        title: "A mining services contractor with a full order book and no cash",
        body: [
          "A Western Australian mining services business, founder-led and just over $20M in revenue, was mobilising for its largest contract to date and could not explain why cash was tight in the strongest year it had ever had. The board pack was a P&L and an aged debtors report. Nothing showed contract-level performance.",
          "The first block rebuilt the view. Contract-level margin, mobilisation cost recovery, unclaimed variations and standing overhead were put on a single page. Two findings dominated: a material sum of legitimate variation work had never been claimed because nobody owned the claim process, and support overhead added during the previous cycle had never been resized.",
          "A variation register with a named owner and a weekly review went in immediately, and the overhead review ran over the following month. The founder kept the contract, kept the crews, and stopped funding the client's scope changes out of his own working capital. The weekly scorecard now carries unclaimed variation value as a standing line.",
        ],
      }}
      relatedLinks={[
        {
          to: "/services/performance-discipline",
          label: "Performance and Commercial Discipline",
          note: "Contract-level margin, variation claims and quote-to-actual comparison, which is where most WA contracting margin is lost.",
        },
        {
          to: "/services/operating-system",
          label: "Operating System and Governance",
          note: "The weekly cadence and scorecard that keep roster utilisation and overhead in view between site blocks.",
        },
        {
          to: "/scan",
          label: "Operating Scan",
          note: "A free self-assessment that takes about six minutes and returns where your constraint most likely sits.",
        },
      ]}
      cta={{
        heading: "Book a Perth on-site block",
        body: "A thirty-minute fit call sets the scope. If a retainer follows, it runs as a three-day Perth block roughly monthly with weekly remote sessions in between, and travel time is absorbed in the price; for interstate work, flights and accommodation are arranged by the client or charged at cost.",
        linkLabel: "Book a fit call",
      }}
      faqs={faqs}
    />
  );
}
